/* eslint-disable no-undef */
/* eslint-disable camelcase */
import { Suggestion } from 'use-places-autocomplete';
import { defaults } from '@sendpayments/js-utils';

export type Address = {
  streetAddressUnitNumber?: string;
  streetAddressName?: string;
  streetAddressType?: string;
  streetAddressNumber?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
};

/**
 * Return display-able values of suggestions.
 */
export const renderSuggestions = (data: Suggestion[]) =>
  data?.map((suggestion) => {
    const {
      place_id,
      structured_formatting: { main_text, secondary_text },
    } = suggestion;

    return { name: `${main_text} ${secondary_text ?? ''}`, value: place_id };
  });

/**
 * Get string value of a matched suggestion.
 */
export const getDisplayValueFromId = (id: string, data: Suggestion[]) => {
  const item = data.find((suggestion) => suggestion.place_id === id);
  if (!item) {
    return '';
  }
  return `${item.structured_formatting?.main_text} ${item.structured_formatting?.secondary_text ?? ''}`;
};

/**
 * Gets an address from a selected place.
 */
export const getAddressDetailsFromPlaceId = async (placeId: string): Promise<Address> => {
  // safety check
  if (!placeId || (placeId && placeId.trim() === '')) {
    return {};
  }

  const request = {
    placeId,
    fields: ['address_component'],
  };

  return new Promise((resolve) => {
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(request, (place, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        const address = getAddressFromGoogleApi(place);
        resolve(address);
      }
    });
  });
};

/**
 * Takes a place result and splits it out into the data we need
 */
const getAddressFromGoogleApi = (address: google.maps.places.PlaceResult): Address => {
  if (address && address.address_components) {
    const data: Address = {};
    for (let i = 0, iLen = address.address_components?.length; i < iLen; i++) {
      if (address.address_components[i].types.includes('route')) {
        data.streetAddressName = address.address_components[i].long_name;
        defaults.streetTypes.forEach((element: any) => {
          if (address.address_components?.[i].long_name.includes(element[0])) {
            // eslint-disable-next-line prefer-destructuring
            data.streetAddressType = element[0];
            data.streetAddressName = address.address_components[i].long_name.replace(element[0], '').trim();
          }
        });
      }
      if (address.address_components[i].types.includes('street_number')) {
        data.streetAddressNumber = address.address_components[i].short_name;
      }
      if (address.address_components[i].types.includes('locality')) {
        data.city = address.address_components[i].short_name;
      }
      if (address.address_components[i].types.includes('administrative_area_level_1')) {
        data.state = address.address_components[i].short_name;
      }
      if (address.address_components[i].types.includes('postal_code')) {
        data.postcode = address.address_components[i].short_name;
      }
      if (address.address_components[i].types.includes('country')) {
        data.country = address.address_components[i].long_name;
      }
    }
    return data;
  }
  return {};
};
